// Relative-time helpers for notice timestamps (PRD 08 §4.2).
//
// Pure functions — no i18n. Callers map `diffInDays` to localized text
// ("오늘" / "N일 전" / "N일 후") and append it to `formatAbsoluteDateTime`.
// The diff is computed against the real current time (product decision).

const pad = (n: number): string => String(n).padStart(2, '0');

// Render every timestamp in Korea Standard Time (Asia/Seoul, UTC+09:00) — the project stores KST
// (DECISION-TIMEZONE-001), and PRD 08 §4.2 requires an absolute `YYYY.MM.DD HH:MM`. Formatting with
// the browser's local zone (getHours()/getFullYear()) would show a different wall-clock on a machine
// that is not in KST (a dev machine in Vietnam, UTC+07, would render 09:00Z as 16:00 instead of the
// spec's 18:00). Pinning the zone here makes the displayed time identical everywhere.
const KST_PARTS = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Seoul',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/** Format an ISO datetime as `YYYY.MM.DD HH:MM` in KST (Asia/Seoul). Returns "-" on bad input. */
export function formatAbsoluteDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  // Intl gives the correct KST calendar parts regardless of the host machine's zone; pull them out by
  // type so the exact `YYYY.MM.DD HH:MM` shape (dots + space, 24-hour) stays under our control.
  const parts = KST_PARTS.formatToParts(d);
  const get = (type: string): string =>
    parts.find((p) => p.type === type)?.value ?? '00';
  const hour = get('hour') === '24' ? '00' : get('hour'); // Intl can emit "24" at midnight
  return `${get('year')}.${get('month')}.${get('day')} ${hour}:${get('minute')}`;
}

/**
 * Whole-day difference between `now` and the given ISO datetime, by calendar day.
 * Positive = in the past (N days ago), 0 = today, negative = in the future.
 * Returns `null` on invalid input.
 */
export function diffInDays(iso: string, now: Date = new Date()): number | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const startOf = (date: Date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  return Math.round((startOf(now) - startOf(d)) / 86_400_000);
}
